/**
 * 用户 AI 报告历史 — 显示最近 20 条,点击加载到 ReportView,🗑️ 删除单条
 * 仅登录用户可见(由父组件用 `user && <ReportHistory />` 控制)
 */
import { useEffect, useState } from 'react'
import {
  listReports, getReport, deleteReport,
  type ReportSummary, type AgentReport, type AgentInput,
  ApiError, translateError,
} from '../lib/api'

interface Props {
  /** 加载历史后,触发父组件 setReport() 把历史报告渲染到 ReportView */
  onLoad: (r: AgentReport) => void
  /** 加载历史后,把当时的表单值带回填写界面 */
  onLoadForm?: (input: AgentInput) => void
  /** 外部通知重新拉取(如新报告生成后 +1) */
  refreshKey?: number
}

export default function ReportHistory({ onLoad, onLoadForm, refreshKey }: Props) {
  const [reports, setReports] = useState<ReportSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingId, setLoadingId] = useState<number | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const refresh = async () => {
    setLoading(true); setErr(null)
    try {
      const r = await listReports()
      setReports(r.reports)
    } catch (e) {
      setErr(e instanceof ApiError ? translateError(e) : String((e as Error).message))
    } finally { setLoading(false) }
  }

  useEffect(() => { refresh() }, [refreshKey])

  const handleLoad = async (id: number) => {
    setLoadingId(id); setErr(null)
    try {
      const r = await getReport(id)
      onLoad(r.report.report)
      onLoadForm?.(r.report.report.input)
      // 滚到报告区(给用户视觉反馈:加载好了)
      requestAnimationFrame(() => {
        document.getElementById('report-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    } catch (e) {
      // 该条已被人删了 / 数据库没了 → 静默剔出列表,不打扰
      if (e instanceof ApiError && e.code === 'not_found') {
        setReports(rs => rs.filter(r => r.id !== id))
      } else {
        setErr(e instanceof ApiError ? translateError(e) : String((e as Error).message))
      }
    } finally { setLoadingId(null) }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('确定删掉这份报告?删了不能恢复。')) return
    setErr(null)
    try {
      await deleteReport(id)
      setReports(rs => rs.filter(r => r.id !== id))
    } catch (e) {
      setErr(e instanceof ApiError ? translateError(e) : String((e as Error).message))
    }
  }

  return (
    <details className="card" open style={{ padding: '0.75rem 1rem', marginBottom: '1rem' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 600, color: 'var(--ink)' }}>
        📂 历史方案{loading ? ' (加载中…)' : ` (${reports.length})`}
      </summary>
      {err && <div className="alert-err" style={{ marginTop: '.5rem', fontSize: 'var(--fs-capt)' }}>{err}</div>}
      {!loading && reports.length === 0 && (
        <div style={{ color: 'var(--ink3)', fontSize: 'var(--fs-capt)', marginTop: '.5rem' }}>
          还没有生成过。填表后点"让 AI 帮我看"会自动存到这里(最多保留 20 条)。
        </div>
      )}
      {reports.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '.5rem 0 0' }}>
          {reports.map(r => (
            <li key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', padding: '.375rem 0', borderBottom: '1px solid var(--rule)' }}>
              <button
                onClick={() => handleLoad(r.id)}
                disabled={loadingId === r.id}
                className="btn-ghost no-underline"
                style={{ flex: 1, textAlign: 'left', fontSize: 'var(--fs-capt)', padding: '.25rem .5rem' }}
              >
                {loadingId === r.id ? '加载中…' : r.title}
              </button>
              <button
                onClick={() => handleDelete(r.id)}
                style={{ fontSize: 'var(--fs-capt)', padding: '.25rem .5rem', background: 'none', border: 'none', color: 'var(--ink3)', cursor: 'pointer' }}
                title="删除"
                onMouseOver={e => { (e.currentTarget as HTMLElement).style.color = 'var(--accent)' }}
                onMouseOut={e => { (e.currentTarget as HTMLElement).style.color = 'var(--ink3)' }}
              >
                🗑️
              </button>
            </li>
          ))}
        </ul>
      )}
    </details>
  )
}
